<template>
  <div class="discover-categories-page">
    <div class="discover-categories-page__inner">
      <!-- Barra de ferramentas: busca + ordenação + situação -->
      <div class="search-toolbar">
        <div class="search-box">
          <span class="search-box__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </span>
          <input
            v-model="searchQuery"
            type="search"
            class="search-box__input"
            placeholder="Pesquisar obras"
            aria-label="Pesquisar obras"
            autocomplete="off"
            @input="searchFilter($event.target.value)"
            @keyup.enter="searchProjects()"
            @focus="searchFocused = true"
            @blur="closeSuggestions"
          />
          <div v-if="searchFocused && filteredResults.length" class="search-suggestions">
            <router-link
              v-for="(item, index) in filteredResults"
              :key="index"
              :to="projectPath(item)"
              class="search-suggestion"
            >
              <img :src="item.project_thumb" :alt="item.name" />
              <span class="search-suggestion__name">{{ item.name }}</span>
            </router-link>
          </div>
        </div>

        <div class="select-box">
          <select v-model="sortBy" class="select-box__control" aria-label="Ordenar por" @change="searchProjects()">
            <option value="recent">Mais recentes</option>
            <option value="oldest">Mais antigos</option>
            <option value="trending">Em alta</option>
            <option v-for="cat in typeCategories" :key="'category-' + cat.id" :value="'category-' + cat.id">{{ cat.name }}</option>
          </select>
          <span class="select-box__chevron">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </span>
        </div>

        <div class="select-box">
          <select v-model="status" class="select-box__control" aria-label="Situação da obra" @change="searchProjects()">
            <option value="">Todas</option>
            <option value="ongoing">Em andamento</option>
            <option value="finished">Finalizada</option>
            <option v-for="tag in tagsTypes" :key="'status-type-' + tag.id" :value="'type-' + tag.id">{{ tag.name }}</option>
          </select>
          <span class="select-box__chevron">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </span>
        </div>
      </div>

      <!-- Categorias (gêneros) -->
      <ChipScroll label="Categorias">
        <button
          class="filter-chip"
          :class="{ 'filter-chip--active': gender_id === null }"
          @click="handleFilter(null, 'gender')"
        >
          Todos
        </button>
        <button
          v-for="tag in tags"
          :key="'gender-' + tag.id"
          class="filter-chip"
          :class="{ 'filter-chip--active': gender_id === tag.id }"
          @click="handleFilter(tag.id, 'gender')"
        >
          {{ tag.name }}
        </button>
      </ChipScroll>

      <!-- Idiomas -->
      <div v-if="tagsLang.length" class="chip-group">
        <span class="chip-group__label">Idioma</span>
        <ChipScroll label="Idioma">
          <button
            v-for="tag in tagsLang"
            :key="'lang-' + tag.id"
            class="filter-chip filter-chip--soft"
            :class="{ 'filter-chip--active': language_id === tag.id }"
            @click="handleFilter(tag.id, 'language')"
          >
            {{ tag.name }}
          </button>
        </ChipScroll>
      </div>

      <!-- Resultados -->
      <div v-if="projects.length" class="results">
        <div class="results__bar">
          <span class="results__count">{{ totalResults }} {{ totalResults === 1 ? "obra" : "obras" }}</span>
        </div>
        <div class="results__grid">
          <div v-for="(result, index) in projects" :key="index" class="results__item">
            <HomeProjectCard :project="result" />
          </div>
        </div>
      </div>
      <div v-else class="no-results">
        <p>Nenhum resultado encontrado.</p>
      </div>

      <div v-if="totalPages > 1" class="pagination-controls">
        <v-pagination v-model="currentPage" :length="totalPages" color="primary" active-color="pink" @input="searchProjects"></v-pagination>
      </div>
    </div>
  </div>
</template>

<script>
import HomeProjectCard from '@/components/home/cards/HomeProjectCard.vue';
import ChipScroll from '@/components/ChipScroll.vue';
import { normalizeProjects } from '@/utils/homeProjectNormalizers';
import { getGenders, getLanguages, getProjectType, getSearchProjects, getCategorys } from '../server/workService.js';
import { projectPath } from '@/utils/projectRoutes';
import { pickFilterCategories } from '@/utils/projectCategory';

import "@/styles/home-page/index.css";

export default {
  name: 'SeriesViews',
  components: {
    HomeProjectCard,
    ChipScroll,
  },
  data() {
    return {
      tags: [],
      tagsLang: [],
      tagsTypes: [],
      tagsCategories: [],
      searchQuery: "",
      projects: [],
      filteredResults: [],
      gender_id: null,
      language_id: null,
      type_id: null,
      sortBy: "recent",
      status: "",
      currentPage: 1,
      totalPages: 1,
      totalResults: 0,
      searchFocused: false,
      searchDebounce: null
    };
  },
  async created() {
    this.tags = await getGenders();
    this.tagsLang = await getLanguages();
    this.tagsTypes = await getProjectType();
    this.tagsCategories = await getCategorys();
  },
  computed: {
    typeCategories() {
      return pickFilterCategories(this.tagsCategories);
    },
  },
  mounted() {
    const urlParams = this.$route.query;
    if (urlParams && urlParams.q) {
      this.searchQuery = urlParams.q;
    }
    this.searchProjects();
  },
  methods: {
    searchFilter(val) {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(async () => {
        if (!val) {
          this.filteredResults = [];
          return;
        }
        try {
          const results = await getSearchProjects({ name: val });
          this.filteredResults = results.data || [];
        } catch (error) {
          console.error("Erro ao buscar sugestões:", error);
        }
      }, 250);
    },
    async searchProjects(page = 1) {
      try {
        let sort = this.sortBy;
        let projectCategoryId = null;
        if (String(this.sortBy).startsWith("category-")) {
          projectCategoryId = Number(String(this.sortBy).split("-")[1]);
          sort = "recent";
        }
        let status = this.status;
        let projectTypeId = this.type_id;
        if (String(this.status).startsWith("type-")) {
          projectTypeId = Number(String(this.status).split("-")[1]);
          status = "";
        }
        const params = {
          name: this.searchQuery,
          gender_id: this.gender_id,
          language_id: this.language_id,
          project_type_id: projectTypeId,
          project_category_id: projectCategoryId,
          sort,
          status,
          page,
        };
        const results = await getSearchProjects(params);
        this.projects = normalizeProjects(results.data);
        this.currentPage = results.current_page;
        this.totalPages = results.last_page;
        this.totalResults = results.total;
      } catch (error) {
        console.error("Erro ao buscar projetos:", error);
      }
    },
    handleFilter(selectedId, type) {
      if (type === 'gender') {
        this.gender_id = this.gender_id === selectedId ? null : selectedId;
      } else if (type === 'language') {
        this.language_id = this.language_id === selectedId ? null : selectedId;
      }
      this.searchProjects();
    },
    closeSuggestions() {
      setTimeout(() => {
        this.searchFocused = false;
      }, 150);
    },
    projectPath,
  },
};
</script>

<style scoped>
.discover-categories-page {
  --field-bg: #251738;
  --border: rgba(255, 255, 255, 0.14);
  --text: #f2ecfa;
  --muted: #b7a3cb;
  --accent: #7a23bd;
  --accent-deep: #57149b;
  --chip-bg: rgba(94, 40, 128, 0.32);
  --chip-bg-hover: rgba(122, 35, 189, 0.26);
  --surface: #1d1230;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.discover-categories-page__inner {
  padding: 4px 0;
}

/* ------------------------------------------------------------------ */
/* Barra de ferramentas (busca + ordenação + situação)                 */
/* ------------------------------------------------------------------ */
.search-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.search-box__icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--muted);
  pointer-events: none;
  display: flex;
}

.search-box__input {
  width: 100%;
  height: 40px;
  padding: 0 16px 0 42px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--field-bg);
  color: var(--text);
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.search-box__input::placeholder {
  color: var(--muted);
}

.search-box__input:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(122, 35, 189, 0.25);
}

.search-box__input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}

/* Sugestões da busca */
.search-suggestions {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  overflow: hidden;
  z-index: 40;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.45);
}

.search-suggestion {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  text-decoration: none;
  color: var(--text);
  transition: background 0.15s ease;
}

.search-suggestion:hover {
  background: var(--chip-bg-hover);
}

.search-suggestion img {
  flex: 0 0 auto;
  width: 32px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
}

.search-suggestion__name {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Selects */
.select-box {
  position: relative;
  flex: 0 0 auto;
}

.select-box__control {
  appearance: none;
  -webkit-appearance: none;
  height: 40px;
  min-width: 130px;
  padding: 0 34px 0 16px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--field-bg);
  color: var(--text);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  color-scheme: dark;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.select-box__control:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(122, 35, 189, 0.25);
}

.select-box__chevron {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--muted);
  pointer-events: none;
  display: flex;
}

/* ------------------------------------------------------------------ */
/* Chips de filtro                                                    */
/* ------------------------------------------------------------------ */
.filter-chip {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--chip-bg);
  color: var(--muted);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
}

.filter-chip:hover {
  background: var(--chip-bg-hover);
  color: var(--text);
  transform: translateY(-1px);
}

.filter-chip--active {
  background: linear-gradient(135deg, #8c2fe0 0%, var(--accent-deep) 100%);
  border-color: transparent;
  color: #fff;
}

.filter-chip--soft {
  text-transform: none;
  letter-spacing: normal;
}

.chip-group {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
}

.chip-group__label {
  flex: 0 0 auto;
  color: var(--muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.chip-group .chip-scroll {
  margin-top: 0;
  flex: 1 1 auto;
  min-width: 0;
}

/* ------------------------------------------------------------------ */
/* Resultados                                                         */
/* ------------------------------------------------------------------ */
.results {
  margin-top: 22px;
}

.results__bar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.results__count {
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
}

.results__grid {
  display: flex;
  flex-wrap: wrap;
}

.results__item {
  display: flex;
  flex-wrap: wrap;
  box-sizing: border-box;
  width: 50%;
  padding: 8px;
  cursor: pointer;
}

@media (min-width: 500px) {
  .results__item {
    width: 33.33%;
  }
}

@media (min-width: 990px) {
  .results__item {
    width: 20%;
  }
}

.no-results {
  text-align: center;
  margin-top: 40px;
  color: var(--muted);
  font-size: 15px;
  font-weight: 600;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* ------------------------------------------------------------------ */
/* Responsivo — mobile                                                */
/* ------------------------------------------------------------------ */
@media (max-width: 768px) {
  .search-toolbar {
    flex-wrap: wrap;
    gap: 10px;
  }

  .search-box {
    flex: 1 1 100%;
  }

  .select-box__control {
    min-width: 0;
    width: auto;
  }
}
</style>